import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type AssigneesSectionLazyFragment, EditIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {EditIssueFieldsSection} from '@github-ui/issue-metadata/FieldsSection'
import {EditIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {EditIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {EditIssueProjectsSection, ProjectsSectionFallback} from '@github-ui/issue-metadata/ProjectsSection'
import {EditIssueIssueTypeSection} from '@github-ui/issue-metadata/TypesSection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {clsx} from 'clsx'
import {Suspense, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueSidebarLazySections$key} from './__generated__/IssueSidebarLazySections.graphql'
import type {IssueSidebarPrimaryQuery$key} from './__generated__/IssueSidebarPrimaryQuery.graphql'
import type {IssueSidebarSecondary$key} from './__generated__/IssueSidebarSecondary.graphql'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {FIXED_HEADER_HEIGHT} from './header/constants/sticky-elements'
import styles from './IssueSidebar.module.css'
import {IssueSidebarLazySections, IssueSidebarLazySectionsFallback} from './IssueSidebarLazySections'
import type {OptionConfig} from './OptionConfig'
import {OptionsSection} from './sections/OptionsSection'

type IssueSidebarProps = {
  sidebarKey: IssueSidebarPrimaryQuery$key
  sidebarSecondaryKey?: IssueSidebarSecondary$key | IssueSidebarLazySections$key
  optionConfig: OptionConfig
  viewer: IssueViewerViewer$data | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueSidebarSecondary = graphql`
  fragment IssueSidebarSecondary on Issue {
    repository {
      viewerCanPush
      isLocked
    }
    isTransferInProgress
    suggestedActors(first: 10) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueSidebarPrimaryGraphqlQuery = graphql`
  fragment IssueSidebarPrimaryQuery on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    ...AssigneesSectionFragment
    ...LabelsSectionFragment
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    ...MilestonesSectionFragment
    ...OptionsSectionFragment
    ...TypesSectionFragment
    ...FieldsSectionFragment
  }
`

export function IssueSidebar({sidebarKey, sidebarSecondaryKey, optionConfig, viewer}: IssueSidebarProps) {
  const issue = useFragment(IssueSidebarPrimaryGraphqlQuery, sidebarKey)
  const issueSecondary = useFragment(IssueSidebarSecondary, sidebarSecondaryKey as IssueSidebarSecondary$key)
  const modernUx = isFeatureEnabled('issues_modern_ux')

  const sidebar = useMemo(() => {
    const viewerCanPush = issueSecondary?.repository?.viewerCanPush ?? false
    const isRepoLocked = issueSecondary?.repository?.isLocked ?? false
    const readonly = optionConfig.isReadonly || isRepoLocked

    return (
      <div
        className={clsx(styles.sidebarContent, modernUx && styles.sidebarContentModernUx)}
        data-modern-ux={modernUx || undefined}
      >
        <EditIssueAssigneesSection
          issue={issue}
          viewer={viewer}
          // RELAY-CLEANUP(IssueViewerSecondaryViewQuery): the still-Relay secondary fragment shape for
          // `suggestedActors` is wider than the migrated plain-props type. Remove when
          // IssueViewerSecondaryViewQuery leaves Relay.
          lazyKey={(issueSecondary ?? undefined) as AssigneesSectionLazyFragment}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueLabelsSection
          issue={issue}
          canCreateLabel={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueIssueTypeSection
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueFieldsSection
          issue={issue}
          onUpdate={optionConfig.onIssueUpdate}
          inProjectsPage={!!optionConfig.selectedProjectId}
          readonly={readonly}
        />
        <Suspense fallback={<ProjectsSectionFallback />}>
          <ErrorBoundary fallback={<ProjectsSectionFallback />}>
            <EditIssueProjectsSection
              selectedProjectId={optionConfig.selectedProjectId}
              allowedProjectOwner={optionConfig.allowedProjectOwner}
              issueOrPullRequest={issue}
              onIssueUpdate={optionConfig.onIssueUpdate}
              onRemovedFromProject={optionConfig.onRemovedFromProject}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={readonly}
            />
          </ErrorBoundary>
        </Suspense>
        <EditIssueMilestonesSection
          issue={issue}
          canCreateMilestone={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <Suspense fallback={<IssueSidebarLazySectionsFallback />}>
          <IssueSidebarLazySections
            onLinkClick={optionConfig.onLinkClick}
            onIssueUpdate={optionConfig.onIssueUpdate}
            insideSidePanel={optionConfig.insideSidePanel}
            issueSidebarSecondaryKey={sidebarSecondaryKey as IssueSidebarLazySections$key}
            viewer={viewer}
            readonly={readonly}
          />
        </Suspense>
        {!readonly && (
          <OptionsSection issue={issue} optionsSectionSecondary={issueSecondary} optionConfig={optionConfig} />
        )}
      </div>
    )
  }, [issue, issueSecondary, optionConfig, sidebarSecondaryKey, viewer, modernUx])

  return <StickySidebar topOffset={FIXED_HEADER_HEIGHT}>{sidebar}</StickySidebar>
}
